/**
 * VoiceTransactionButton — Mic button that triggers speech-to-transaction flow.
 * Can be used standalone (SpeedDial) or inline (TransactionForm).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Fade,
  useTheme,
  keyframes,
} from '@mui/material';
import { Mic as MicIcon, MicOff as MicOffIcon } from '@mui/icons-material';
import useVoiceInput from '../../hooks/useVoiceInput';
import { voiceApi } from '../../services/api';
import VoiceTransactionSheet from './VoiceTransactionSheet';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.5); }
  70% { box-shadow: 0 0 0 14px rgba(244, 67, 54, 0); }
  100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
`;

export default function VoiceTransactionButton({
  categories = [],
  onTransactionSave,
  onEditMore,
  variant = 'fab', // 'fab' (SpeedDial) or 'inline' (TransactionForm)
  size = 'medium',
  onClose,
  autoStart = false,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const {
    isListening,
    transcript,
    interimTranscript,
    error: speechError,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
  } = useVoiceInput();

  const [isParsing, setIsParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [autoStarted, setAutoStarted] = useState(false);

  // Auto-start listening when variant=fab and mounted
  useEffect(() => {
    if ((variant === 'fab' || autoStart) && isSupported && !autoStarted) {
      setAutoStarted(true);
      const timer = setTimeout(() => startListening(), 300);
      return () => clearTimeout(timer);
    }
  }, [variant, autoStart, isSupported, autoStarted, startListening]);

  // When speech completes with a transcript, send to backend for parsing
  useEffect(() => {
    if (!isListening && transcript && !isParsing && !sheetOpen) {
      parseTranscript(transcript);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListening, transcript]);

  const parseTranscript = useCallback(async (text) => {
    setIsParsing(true);
    try {
      const result = await voiceApi.parseTransaction(text, i18n.language);
      if (result?.success && result.parsed) {
        setParsedData(result.parsed);
        setSheetOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: t('voice.couldNotUnderstand', 'Could not understand. Try again.'),
          severity: 'warning',
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('voice.couldNotUnderstand', 'Could not understand. Try again.'),
        severity: 'error',
      });
    } finally {
      setIsParsing(false);
    }
  }, [i18n.language, t]);

  const handleMicClick = useCallback(() => {
    if (!isSupported) {
      setSnackbar({
        open: true,
        message: t('voice.notSupported', 'Voice input not supported in this browser'),
        severity: 'warning',
      });
      return;
    }
    if (isListening) {
      stopListening();
    } else {
      resetTranscript();
      setParsedData(null);
      startListening();
    }
  }, [isSupported, isListening, stopListening, resetTranscript, startListening, t]);

  const handleSave = useCallback((data) => {
    setSheetOpen(false);
    resetTranscript();
    if (onTransactionSave) onTransactionSave(data);
    if (onClose) onClose();
  }, [onTransactionSave, onClose, resetTranscript]);

  const handleEditMore = useCallback((data) => {
    setSheetOpen(false);
    resetTranscript();
    if (onEditMore) onEditMore(data);
    if (onClose) onClose();
  }, [onEditMore, onClose, resetTranscript]);

  const handleRetry = useCallback(() => {
    setSheetOpen(false);
    setParsedData(null);
    resetTranscript();
    setTimeout(() => startListening(), 300);
  }, [resetTranscript, startListening]);

  const handleSheetClose = useCallback(() => {
    setSheetOpen(false);
    setParsedData(null);
    resetTranscript();
  }, [resetTranscript]);

  // Show speech error as snackbar
  useEffect(() => {
    if (speechError) {
      setSnackbar({ open: true, message: speechError, severity: 'error' });
    }
  }, [speechError]);

  const displayText = interimTranscript || transcript || '';
  const isActive = isListening || isParsing;

  if (variant === 'inline') {
    return (
      <>
        <IconButton
          onClick={handleMicClick}
          color={isListening ? 'error' : 'primary'}
          size={size}
          disabled={isParsing}
          sx={{
            animation: isListening ? `${pulse} 1.5s infinite` : 'none',
          }}
          title={t('voice.tapToSpeak', 'Tap to speak')}
        >
          {isParsing ? (
            <CircularProgress size={20} />
          ) : isListening ? (
            <MicIcon />
          ) : (
            <MicIcon />
          )}
        </IconButton>

        {/* Inline transcript display */}
        {isActive && displayText && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontStyle: 'italic' }}>
            {displayText}
          </Typography>
        )}

        <VoiceTransactionSheet
          open={sheetOpen}
          onClose={handleSheetClose}
          parsedData={parsedData}
          categories={categories}
          onSave={handleSave}
          onEditMore={handleEditMore}
          onRetry={handleRetry}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // FAB variant — full-screen overlay
  return (
    <>
      {/* Listening overlay */}
      <Fade in={isActive && !sheetOpen}>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(0,0,0,0.85)'
              : 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(10px)',
            pointerEvents: isActive && !sheetOpen ? 'auto' : 'none',
          }}
          onClick={isListening ? stopListening : undefined}
        >
          {/* Pulsing mic circle */}
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isListening ? 'error.main' : 'primary.main',
              animation: isListening ? `${pulse} 1.5s infinite` : 'none',
              transition: 'background-color 0.3s',
              mb: 3,
            }}
          >
            {isParsing ? (
              <CircularProgress size={48} sx={{ color: '#fff' }} />
            ) : (
              <MicIcon sx={{ fontSize: 48, color: '#fff' }} />
            )}
          </Box>

          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            {isParsing
              ? t('voice.processing', 'Processing...')
              : isListening
                ? t('voice.listening', 'Listening...')
                : t('voice.tapToSpeak', 'Tap to speak')}
          </Typography>

          {displayText && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 300, textAlign: 'center', px: 2 }}
            >
              "{displayText}"
            </Typography>
          )}

          {!isListening && !isParsing && (
            <Typography variant="caption" color="text.disabled" sx={{ mt: 2 }}>
              {t('voice.speakExample', 'Try saying: "I spent 45 on groceries yesterday"')}
            </Typography>
          )}
        </Box>
      </Fade>

      <VoiceTransactionSheet
        open={sheetOpen}
        onClose={handleSheetClose}
        parsedData={parsedData}
        categories={categories}
        onSave={handleSave}
        onEditMore={handleEditMore}
        onRetry={handleRetry}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
